/**
 * Кадр подробно (прежний `shot.html.j2`): слоты с уровнем наследования и
 * привязкой из библиотеки проекта, промпт, генерации (запуск, лента и полный
 * разбор «чем это получено»), история правок, комментарии кадра.
 *
 * Слот, заданный здесь, переопределяет только себя — остальные наследуются
 * от проекта (а тот — от шаблона).
 *
 * Кадр видео-проекта — ещё и клип хронологии: в «Промпте кадра» поля длительности, камеры,
 * видео-промпта и перехода, ниже — панель «Клипы кадра» по моделям.
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import clsx from "clsx";
import { Link2, Save } from "lucide-react";
import { useEffect, useState, type KeyboardEvent } from "react";
import { useParams } from "react-router";

import { apiFetch } from "@/lib/api";
import { mediaUrl, when } from "@/lib/media";
import type { Fact, FieldResult, ResolvedSlot, ShotView, Slot } from "@/lib/project";
import {
  errorText,
  groupInOrder,
  LEVEL_LABEL,
  postJson,
  qk,
  useGens,
  usd,
  whenFull,
} from "@/lib/project";
import { SHOT_TRANSITION_LABEL, videoKeys, type ShotFace, type ShotTransition } from "@/lib/video";
import { Comments } from "@/components/project/Comments";
import { GenPanel } from "@/components/project/GenPanel";
import { GenPreview, type PreviewTarget } from "@/components/project/GenPreview";
import { GensFeed } from "@/components/project/GensFeed";
import { ShotUpload } from "@/components/project/ShotUpload";
import { Btn, Crumbs, Empty, ErrorLine, FactsLine, Hint, inputClass, PageState, Panel, selectClass } from "@/components/project/ui";
import { FaceToggle } from "@/components/video/bits";
import { ShotClips } from "@/components/video/ShotClips";

export function ShotPage() {
  const params = useParams();
  const pid = Number(params.projectId);
  const sid = Number(params.shotId);
  const client = useQueryClient();
  const query = useQuery({
    queryKey: qk.shot(pid, sid),
    queryFn: () => apiFetch<ShotView>(`/api/projects/${pid}/shots/${sid}`),
    enabled: Number.isFinite(pid) && Number.isFinite(sid),
  });
  const [preview, setPreview] = useState<PreviewTarget | null>(null);
  const [uploadOpen, setUploadOpen] = useState(false);
  const data = query.data;

  if (!data) return <PageState loading={query.isPending} error={query.error ? errorText(query.error) : null} />;

  const { project, shot } = data;
  const isVideo = project.project_duration_ms !== null;
  const fromFrame = project.project_source_variant_id !== null;
  // Правка кадра видна и на ленте проекта (и в хронологии ролика) — их кэш тоже перечитываем.
  const reload = () =>
    Promise.all([
      client.invalidateQueries({ queryKey: qk.shot(pid, sid) }),
      client.invalidateQueries({ queryKey: qk.project(pid) }),
      ...(isVideo ? [client.invalidateQueries({ queryKey: videoKeys.poll(pid) })] : []),
    ]);
  const freeSlots = data.slots.filter((s) => !data.resolved.some((r) => r.slot_id === s.id));

  return (
    <div className={preview ? "p-5 xl:pr-[460px]" : "p-5"}>
      <Crumbs
        items={[
          { label: "проекты", to: "/projects" },
          { label: project.project_name, to: `/project/${pid}` },
          { label: `кадр ${shot.shot_order}` },
        ]}
      />
      <h1 className="text-[20px] font-semibold tracking-[-0.01em] text-fg" data-annot={`кадр ${shot.id}`}>
        {shot.shot_order}. {shot.shot_title}
      </h1>
      {fromFrame ? null : (
        <p className="mt-1 mb-4 text-[12.5px] text-fg-muted">
          Слот, заданный здесь, переопределяет только себя — остальные наследуются от проекта.
        </p>
      )}

      <div className="grid items-start gap-4 lg:grid-cols-[minmax(320px,440px)_minmax(0,1fr)]">
        <div className="grid gap-4">
          {/* Слоты — конструктор кадра из ФОТО ТОВАРА (каскад шаблон → проект → кадр). У ролика
              «из кадра» кадры это ракурсы исходной картинки, слоты в них не участвуют и стоят
              пустыми — задача Оли №343 «объясни смысл… мне кажется избыточно», подтверждено №346. */}
          {fromFrame ? null : <SlotsPanel pid={pid} sid={sid} data={data} onChanged={reload} />}
          <Panel
            title="Фото в кадр"
            aside={
              <button type="button" className="text-accent-text hover:underline" onClick={() => setUploadOpen((v) => !v)}>
                {uploadOpen ? "свернуть" : "загрузить"}
              </button>
            }
          >
            {uploadOpen ? (
              <ShotUpload
                pid={pid}
                sid={sid}
                freeSlots={freeSlots}
                allSlots={data.slots}
                armed
                onArm={() => undefined}
                onUploaded={() => void reload()}
              />
            ) : (
              <Hint>Файлы, перетаскивание или Ctrl-V: первый файл встанет в выбранный слот кадра.</Hint>
            )}
          </Panel>
        </div>

        <div className="grid min-w-0 gap-4">
          <PromptPanel
            key={`${shot.id}:${shot.date_modify}`}
            pid={pid}
            sid={sid}
            title={shot.shot_title}
            prompt={shot.shot_prompt ?? ""}
            facts={data.facts}
            video={
              isVideo
                ? {
                    duration_ms: shot.shot_duration_ms,
                    camera: shot.shot_camera ?? "",
                    video_prompt: shot.shot_video_prompt ?? "",
                    transition: shot.shot_transition ?? "",
                    face: shot.shot_face ?? "visible",
                  }
                : null
            }
            onSaved={reload}
          />

          {isVideo ? <ShotClips pid={pid} sid={sid} /> : null}

          {/* Полная история генераций — здесь, а не в раскадровке: там полоса
              миниатюр отвечает на «что получилось», а «чем именно это получено»
              (какие входы, сколько стоило) спрашивают, когда уже открыли кадр. */}
          <Panel title="Генерации кадра" annot="генерации кадра">
            <GenPanel pid={pid} sid={sid} inputs={data.inputs} options={data} />
            <GensFeed
              pid={pid}
              sid={sid}
              seed={{ data: { gens: data.gens, pending: data.pending, error: "" }, at: query.dataUpdatedAt }}
              openVariant={preview?.vid ?? null}
              onOpen={(vid) => setPreview({ sid, vid, where: `${shot.shot_order}. ${shot.shot_title}` })}
            />
            <Batches pid={pid} sid={sid} />
          </Panel>

          <HistoryPanel history={data.history} />

          <Comments
            title="Комментарии к кадру"
            comments={data.comments}
            target={{ project_shot_id: sid }}
            placeholder="что поправить в этом кадре"
            onAdded={() => client.invalidateQueries({ queryKey: qk.shot(pid, sid) })}
          />
        </div>
      </div>

      {preview ? <GenPreview pid={pid} target={preview} onChange={setPreview} /> : null}
    </div>
  );
}

// ---------------------------------------------------------------------------
// слоты
// ---------------------------------------------------------------------------

function SlotsPanel({
  pid,
  sid,
  data,
  onChanged,
}: {
  pid: number;
  sid: number;
  data: ShotView;
  onChanged: () => Promise<unknown>;
}) {
  const resolved = new Map(data.resolved.map((r) => [r.slot_id, r]));
  const assets = [...data.library.assigned, ...data.library.loose];

  return (
    <Panel title="Слоты кадра" annot="слоты кадра">
      <div className="grid gap-2">
        {data.slots.map((s) => (
          <SlotRow
            key={s.id}
            pid={pid}
            sid={sid}
            slot={s}
            resolved={resolved.get(s.id)}
            assets={assets}
            onChanged={onChanged}
          />
        ))}
      </div>
    </Panel>
  );
}

function SlotRow({
  pid,
  sid,
  slot,
  resolved,
  assets,
  onChanged,
}: {
  pid: number;
  sid: number;
  slot: Slot;
  resolved: ResolvedSlot | undefined;
  assets: ShotView["library"]["assigned"];
  onChanged: () => Promise<unknown>;
}) {
  const client = useQueryClient();
  const [assetId, setAssetId] = useState(0);
  const [level, setLevel] = useState<"shot" | "project">("shot");
  const chosen = assets.find((a) => a.id === assetId);

  const bind = useMutation({
    mutationFn: () =>
      postJson<{ resolved: ResolvedSlot[] }>(`/api/projects/${pid}/shots/${sid}/bind`, {
        slot_code: slot.slot_code,
        project_asset_id: assetId,
        level,
      }),
    onSuccess: async (result) => {
      client.setQueryData<ShotView>(qk.shot(pid, sid), (old) => (old ? { ...old, resolved: result.resolved } : old));
      setAssetId(0);
      // Привязка меняет и входы генерации, и слоты на ленте проекта.
      await onChanged();
    },
  });

  return (
    <div
      data-annot={`слот ${slot.slot_code}`}
      className="grid grid-cols-[minmax(0,1fr)_auto] gap-x-3 gap-y-2 border-b border-border pb-2.5 last:border-b-0"
    >
      <div className="min-w-0">
        <div className="font-medium text-fg">{slot.slot_name}</div>
        <div className="font-mono text-[11px] text-fg-faint">{slot.slot_code}</div>
        <div className="mt-1">
          {resolved ? (
            <span
              className={clsx(
                "rounded-full px-2 py-0.5 text-[11px]",
                resolved.level === "shot" ? "bg-ok-bg text-ok" : "bg-accent-dim text-accent-text",
              )}
            >
              {LEVEL_LABEL[resolved.level]}
            </span>
          ) : (
            <span className="rounded-full bg-warn-bg px-2 py-0.5 text-[11px] text-warn">пусто</span>
          )}
        </div>
      </div>
      <div className="row-span-2 w-[64px]">
        {resolved ? (
          <a href={mediaUrl(resolved.asset_path)} target="_blank" rel="noopener">
            <img
              src={mediaUrl(resolved.asset_path)}
              alt={slot.slot_code}
              className="aspect-[3/4] w-full rounded-[4px] border border-border object-cover"
            />
          </a>
        ) : (
          <div className="aspect-[3/4] w-full rounded-[4px] border border-dashed border-border-strong" />
        )}
      </div>
      <form
        className="flex flex-wrap items-center gap-1.5"
        onSubmit={(e) => {
          e.preventDefault();
          if (assetId && !bind.isPending) bind.mutate();
        }}
      >
        <select
          value={assetId}
          onChange={(e) => setAssetId(Number(e.target.value))}
          required
          className={`${selectClass} min-w-0 basis-full`}
        >
          <option value={0}>— выбрать из библиотеки —</option>
          {assets.map((a) => (
            <option key={a.id} value={a.id}>
              #{a.id} {a.slot_name ?? "без слота"}
            </option>
          ))}
        </select>
        {chosen?.asset_path ? (
          <img
            src={mediaUrl(chosen.asset_path)}
            alt=""
            className="h-8 w-6 rounded-[3px] border border-accent object-cover"
          />
        ) : null}
        <select
          value={level}
          onChange={(e) => setLevel(e.target.value === "project" ? "project" : "shot")}
          className={selectClass}
        >
          <option value="shot">в этот кадр</option>
          <option value="project">во весь проект</option>
        </select>
        <Btn type="submit" disabled={!assetId || bind.isPending}>
          <Link2 size={13} />
          Привязать
        </Btn>
        <ErrorLine text={bind.isError ? errorText(bind.error) : ""} className="w-full" />
      </form>
    </div>
  );
}

// ---------------------------------------------------------------------------
// промпт
// ---------------------------------------------------------------------------

/** Подстановка фактов для предпросмотра — как `generate.resolve_text`: неизвестная переменная остаётся собой. */
function resolveText(text: string, facts: Fact[]): string {
  const values = new Map(facts.map((f) => [f.fact_key, f.fact_value]));
  return text.replace(/\{(\w+)\}/g, (whole, key: string) => values.get(key) ?? whole);
}

/** Поля клипа кадра видео-проекта, как их показывает форма. */
type VideoFields = {
  duration_ms: number | null;
  camera: string;
  video_prompt: string;
  transition: ShotTransition | "";
  face: ShotFace;
};

/** «5,5» / «5.5» → мс; пусто → ""; не число → исходный текст (сервер ответит BAD_VALUE по-русски). */
function durationValue(text: string): string {
  const t = text.trim().replace(",", ".");
  if (!t) return "";
  const sec = Number(t);
  return Number.isFinite(sec) ? String(Math.round(sec * 1000)) : text.trim();
}

const secondsText = (ms: number | null) => (ms === null ? "" : String(ms / 1000).replace(".", ","));

function PromptPanel({
  pid,
  sid,
  title,
  prompt,
  facts,
  video,
  onSaved,
}: {
  pid: number;
  sid: number;
  title: string;
  prompt: string;
  facts: Fact[];
  /** Поля клипа — только у видео-проекта. */
  video: VideoFields | null;
  onSaved: () => Promise<unknown>;
}) {
  const [draftTitle, setTitle] = useState(title);
  const [draftPrompt, setPrompt] = useState(prompt);
  const [draftDuration, setDuration] = useState(secondsText(video?.duration_ms ?? null));
  const [draftCamera, setCamera] = useState(video?.camera ?? "");
  const [draftVideoPrompt, setVideoPrompt] = useState(video?.video_prompt ?? "");
  const [draftTransition, setTransition] = useState<ShotTransition | "">(video?.transition ?? "");
  const [draftFace, setFace] = useState<ShotFace>(video?.face ?? "visible");
  const [done, setDone] = useState(false);

  // Что изменилось: [поле ручки, новое значение]. Ручка правит одно поле за раз (история правок
  // пишется по полю), поэтому каждое изменённое уходит своим запросом — и только изменённые.
  const changes: [string, string][] = [];
  if (draftTitle.trim() !== title.trim()) changes.push(["shot_title", draftTitle]);
  if (draftPrompt.trim() !== prompt.trim()) changes.push(["shot_prompt", draftPrompt]);
  if (video) {
    const ms = durationValue(draftDuration);
    if (ms !== (video.duration_ms === null ? "" : String(video.duration_ms))) changes.push(["shot_duration_ms", ms]);
    if (draftCamera.trim() !== video.camera.trim()) changes.push(["shot_camera", draftCamera]);
    if (draftVideoPrompt.trim() !== video.video_prompt.trim()) changes.push(["shot_video_prompt", draftVideoPrompt]);
    if (draftTransition !== video.transition) changes.push(["shot_transition", draftTransition]);
    if (draftFace !== video.face) changes.push(["shot_face", draftFace]);
  }
  const dirty = changes.length > 0;
  const resolved = resolveText(draftPrompt.trim(), facts);

  useEffect(() => {
    if (!done) return;
    const timer = setTimeout(() => setDone(false), 1500);
    return () => clearTimeout(timer);
  }, [done]);

  const save = useMutation({
    mutationFn: async (list: [string, string][]) => {
      const url = `/api/projects/${pid}/shots/${sid}/field`;
      for (const [field, value] of list) await postJson<FieldResult>(url, { field, value });
    },
    onSuccess: async () => {
      setDone(true);
      await onSaved();
    },
    // Половина могла сохраниться (заголовок прошёл, промпт нет) — перечитываем.
    onError: () => void onSaved(),
  });
  const submit = () => {
    if (dirty && !save.isPending) save.mutate(changes);
  };
  const ctrlEnter = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && (e.ctrlKey || e.metaKey)) {
      e.preventDefault();
      submit();
    }
  };
  const area =
    "resize-y rounded-[var(--radius-ui)] border border-border bg-bg px-2.5 py-2 text-[12.5px] leading-[1.5] text-fg outline-none placeholder:text-fg-faint focus:border-accent";

  return (
    <Panel title="Промпт кадра" annot="промпт кадра">
      <form
        className="grid gap-2"
        onSubmit={(e) => {
          e.preventDefault();
          submit();
        }}
      >
        <input
          value={draftTitle}
          onChange={(e) => setTitle(e.target.value)}
          placeholder="заголовок кадра"
          className={inputClass}
        />
        <textarea
          value={draftPrompt}
          onChange={(e) => setPrompt(e.target.value)}
          onKeyDown={ctrlEnter}
          rows={6}
          placeholder="та же девушка в том же платье спускается по мраморной лестнице, тёплый вечерний свет"
          className={area}
        />
        <Hint>
          Конкретику товара в текст не пишем — она подставится из фактов проекта:{" "}
          {facts.length ? <FactsLine facts={facts} /> : "фактов пока нет"}
        </Hint>
        {resolved && resolved !== draftPrompt.trim() ? (
          <div className="rounded-[var(--radius-ui)] border-l-2 border-accent bg-bg px-2 py-1 text-[11.5px] text-fg-faint">
            уйдёт в модель: {resolved}
          </div>
        ) : null}

        {video ? (
          <div className="mt-1 grid gap-2 border-t border-border pt-3" data-annot="поля клипа">
            <div className="text-[12.5px] font-medium text-fg">Клип</div>
            <div className="flex flex-wrap items-center gap-2">
              <label className="flex items-center gap-1.5 text-[12px] text-fg-muted">
                длительность, с
                <input
                  value={draftDuration}
                  onChange={(e) => setDuration(e.target.value)}
                  inputMode="decimal"
                  placeholder="5"
                  title="1–15 с, шаг 0,5"
                  className={`${inputClass} w-[72px] tnum`}
                />
              </label>
              <label className="flex items-center gap-1.5 text-[12px] text-fg-muted">
                переход после кадра
                <select
                  value={draftTransition}
                  onChange={(e) => setTransition(e.target.value === "cut" || e.target.value === "crossfade" ? e.target.value : "")}
                  className={selectClass}
                >
                  <option value="">как у монтажа</option>
                  {(Object.keys(SHOT_TRANSITION_LABEL) as ShotTransition[]).map((t) => (
                    <option key={t} value={t}>
                      {SHOT_TRANSITION_LABEL[t]}
                    </option>
                  ))}
                </select>
              </label>
            </div>
            {/* Отметку лица ставит план ракурса (№342/№346: «не понимаю смысл этого переключателя»),
                руками её трогают редко — поэтому она в подробностях и подписана, зачем нужна. */}
            <details className="text-[12px] text-fg-muted">
              <summary className="cursor-pointer select-none hover:text-fg">
                лицо в кадре: {draftFace === "hidden" ? "нет" : "есть"}
              </summary>
              <div className="mt-1.5 grid gap-1">
                <FaceToggle value={draftFace} onChange={setFace} />
                <Hint>
                  Это не про картинку, а про видео-модель: та, что не принимает кадры с лицом, отдаёт такой кадр
                  запасной. Значение ставится по плану ракурса — поправьте, если на кадре вышло иначе.
                </Hint>
              </div>
            </details>
            <input
              value={draftCamera}
              onChange={(e) => setCamera(e.target.value)}
              placeholder="камера (EN): slow dolly-in / orbit 15° / static"
              maxLength={160}
              className={inputClass}
            />
            <textarea
              value={draftVideoPrompt}
              onChange={(e) => setVideoPrompt(e.target.value)}
              onKeyDown={ctrlEnter}
              rows={4}
              placeholder="видео-промпт (EN): только движение — ткань, волосы, свет, камера"
              className={area}
            />
            <Hint>Длительность 1–15 с, шаг 0,5. Без длительности и видео-промпта клип кадра не запустится.</Hint>
          </div>
        ) : null}

        <div className="flex items-center gap-2">
          <Btn type="submit" tone="primary" disabled={!dirty || save.isPending}>
            <Save size={13} />
            {save.isPending ? "сохраняю…" : "Сохранить"}
          </Btn>
          <Hint>Ctrl+Enter — сохранить</Hint>
          {done ? <span className="text-[12px] text-ok">сохранено</span> : null}
        </div>
        <ErrorLine text={save.isError ? errorText(save.error) : ""} />
      </form>
    </Panel>
  );
}

// ---------------------------------------------------------------------------
// генерации: разбор по прогонам
// ---------------------------------------------------------------------------

/**
 * Прогоны кадра — новые сверху, как и в ленте. Читает ту же ленту, что
 * плитки: опрос обновляет и их, и этот список одним запросом.
 */
function Batches({ pid, sid }: { pid: number; sid: number }) {
  const { data } = useGens(pid, sid);
  const batches = groupInOrder(data?.gens ?? [], (g) => g.generation_id);

  if (!batches.length) return <div className="mt-3"><Empty>Ещё ничего не генерировали.</Empty></div>;

  return (
    <div className="mt-4 grid gap-2 border-t border-border pt-3">
      {batches.map(([gid, rows]) => {
        const g = rows[0];
        if (!g) return null;
        return (
          <div key={gid} className="border-b border-border pb-2 last:border-b-0">
            <div className="flex flex-wrap items-center gap-1.5">
              <b className="tnum">#{gid}</b>
              <span className="text-fg-muted">
                · {g.generation_model} · {g.quality} · {g.size} ·
              </span>
              <span
                className={clsx(
                  "rounded-full px-2 py-0.5 text-[11px]",
                  g.status === "done" ? "bg-ok-bg text-ok" : g.status === "failed" ? "bg-err-bg text-err" : "bg-warn-bg text-warn",
                )}
              >
                {g.status}
              </span>
              {g.generation_cost_actual ? (
                <span className="tnum text-fg-muted">· факт {usd(g.generation_cost_actual, 4)}</span>
              ) : g.generation_cost_estimate ? (
                <span className="tnum text-fg-faint">· оценка {usd(g.generation_cost_estimate, 4)}</span>
              ) : null}
            </div>
            {g.generation_error ? <div className="mt-0.5 text-[12px] text-err">{g.generation_error}</div> : null}
            <div className="mt-0.5 text-[11.5px] text-fg-faint">
              входы: {g.inputs.length ? g.inputs.map((i) => i.label).join(" · ") : "—"}
            </div>
            <div className="text-[11.5px] text-fg-faint">{when(g.date_create)}</div>
          </div>
        );
      })}
    </div>
  );
}

// ---------------------------------------------------------------------------
// история правок
// ---------------------------------------------------------------------------

/** История правок пишется не этим экраном, а `store.update_tracked` в той же транзакции, что сама правка. */
function HistoryPanel({ history }: { history: ShotView["history"] }) {
  const rows = history.filter((h) => h.field_name);
  const show = (v: string | number | null) => (v === null || v === "" ? "—" : String(v));
  return (
    <Panel title="История правок" annot="история правок">
      {rows.length === 0 ? (
        <Empty>Кадр ещё не правили.</Empty>
      ) : (
        <div className="grid gap-2">
          {rows.map((h, n) => (
            <div key={`${h.batch_id}-${h.field_name}-${n}`} className="border-b border-border pb-2 last:border-b-0">
              <div>
                <b>{h.label}</b>: <span className="text-fg-faint line-through decoration-fg-faint/50">{show(h.old)}</span>{" "}
                → <span className="text-fg">{show(h.new)}</span>
              </div>
              <div className="text-[11.5px] text-fg-faint">
                {h.changes_author} · {whenFull(h.date_create)}
              </div>
            </div>
          ))}
        </div>
      )}
    </Panel>
  );
}
